import { Dispatch, SetStateAction, useContext, useState } from "react";
import { defaultLinjeKursistColumns } from "../../../../../config/linjeConfig";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { ModalProvider, ModalTrigger } from "../../../../Modal/Modal";
import { TableBody, TableHead, TableRow } from "../../../../Table/TableBasic";
import IngenRegistreringer from "../../../person/id/IngenRegistreringer";
import OpretNyButtonBlock from "../../../person/id/OpretNyButtonBlock";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import { Checkbox } from "../../../../formcontrols/InputCheckbox";
import { linjeKursistForm } from "../../../../../config/form/linje/linjeKursistFormConfig";
import { cancelLinjeKursistForm } from "../../../../../config/form/linje/cancelLinjeKursistFormConfig";
import { FmulinjeKursist } from "../../../../../interfaces/schema";
import { KursusholdPersonKursistField } from "../../../../../interfaces/kursus";
import { SortBy } from "../../../../../interfaces/table";
import { UserQueryContext } from "../../../../../context/UserContext";
import { DropdownMenuItem } from "../../../../dropdown/DropdownMenu";
import FormModal from "../../../../Modal/modals/FormModal";
import UserRemoveIcon from "../../../../icons/UserRemoveIcon";
import EditIcon from "../../../../icons/EditIcon";
import { editLinjeKursistForm } from "../../../../../config/form/linje/editLinjeKursistFormConfig";
import { orderBy } from "../../../../../util/orderBy";

export default function LinjeKursist({
  id,
  handlinger: { allSelected, selectedIds, setSelectedIds },
  fmulinjeKursist,
  refetch,
}: {
  id: string;
  handlinger: {
    allSelected: boolean;
    selectedIds: string[];
    setSelectedIds: Dispatch<SetStateAction<string[]>>;
  };
  fmulinjeKursist: FmulinjeKursist[];
  refetch: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "KursusWrite");
  const [linjeKursistColumns, setLinjeKursistActiveColumns] = useActiveColumns(
    "linje-linjekursist-active-columns",
    defaultLinjeKursistColumns,
  );
  const [sortBy, setSortBy] = useState<SortBy[]>(defaultSortBy);
  const antal = fmulinjeKursist.filter(
    (p) => p.deltagerStatus !== "AFMELDT",
  ).length;
  // const currentFirma = extractCurrentPersonFirma(person);

  if (!fmulinjeKursist || (fmulinjeKursist as unknown as []).length === 0) {
    return (
      <IngenRegistreringer
        className="my-3 mx-4"
        editConfig={canWrite ? linjeKursistForm(antal, id) : undefined}
        invalidateData={refetch}
      />
    );
  }
  return (
    <ModalProvider>
      <div className="px-2 flex-grow-1 table-scroll">
        <table className="table table--selectable table--selectable-embedded">
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <th className="action-select">
              <label className="form-check-label--wrapped">
                <Checkbox
                  checkboxState={
                    allSelected
                      ? "CHECKED"
                      : selectedIds.length
                        ? "INDETERMINATE"
                        : "EMPTY"
                  }
                  aria-label="Vælg"
                  type="checkbox"
                  className="form-check-input"
                  onChange={(e) =>
                    setSelectedIds(
                      e.target.checked
                        ? fmulinjeKursist.map((pk) => pk.id)
                        : [],
                    )
                  }
                />
              </label>
            </th>
            <>
              {getHeaderActiveColumns(linjeKursistColumns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
            </>
          </TableHead>
          <TableBody>
            {orderBy(fmulinjeKursist, sortBy[0].field, sortBy[0].direction).map(
              (kursist, i) => {
                const checked = selectedIds.indexOf(kursist.id) > -1;
                const deltagerStatus =
                  kursist.deltagerStatusNavigation?.navn.toUpperCase();
                return (
                  <TableRow
                    editConfig={
                      canWrite
                        ? linjeKursistForm(antal, id, kursist.id)
                        : undefined
                    }
                    key={i}
                    className={`${checked ? "selected" : ""}`}
                    invalidateData={refetch}
                    moreActions={
                      deltagerStatus !== "AFMELDT"
                        ? [
                            <DropdownMenuItem asChild key="edit">
                              <FormModal
                                title="Rediger"
                                editConfig={editLinjeKursistForm(id, kursist)}
                                invalidateData={refetch}
                                method={"patch"}
                                triggerElement={
                                  <ModalTrigger className="c-dropdown-menu-item">
                                    <span className="text-primary d-flex align-items-center">
                                      <EditIcon />
                                    </span>{" "}
                                    Rediger deltager
                                  </ModalTrigger>
                                }
                              />
                            </DropdownMenuItem>,
                            <DropdownMenuItem asChild key="afmeld">
                              <FormModal
                                title="Afmeld"
                                editConfig={cancelLinjeKursistForm(id, kursist)}
                                invalidateData={refetch}
                                method={"post"}
                                triggerElement={
                                  <ModalTrigger className="c-dropdown-menu-item">
                                    <span className="text-primary d-flex align-items-center">
                                      <UserRemoveIcon />
                                    </span>{" "}
                                    Afmeld deltager
                                  </ModalTrigger>
                                }
                              />
                            </DropdownMenuItem>,
                          ]
                        : []
                    }
                  >
                    <>
                      <th scope="row" className="action-select">
                        <label
                          className="form-check-label--wrapped"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checkboxState={checked ? "CHECKED" : "EMPTY"}
                            aria-label="Vælg"
                            type="checkbox"
                            className="form-check-input"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds([...selectedIds, kursist.id]);
                              } else {
                                setSelectedIds(
                                  selectedIds.filter((id) => id !== kursist.id),
                                );
                              }
                            }}
                          />
                        </label>
                      </th>
                      {linjeKursistColumns
                        .filter((col) => col.active)
                        .map((col) => {
                          return (
                            <TD key={col.field} col={col} rowData={kursist} />
                          );
                        })}
                    </>
                  </TableRow>
                );
              },
            )}
          </TableBody>
        </table>
      </div>
      <ColumnEditorModal
        defaultColumns={defaultLinjeKursistColumns}
        columns={linjeKursistColumns}
        setActiveColumns={setLinjeKursistActiveColumns}
      />
      <div className="flex-shrink-0 border-top bg-white shadow">
        <OpretNyButtonBlock
          className="d-grid d-md-flex justify-content-md-end p-1 pe-3"
          editConfig={canWrite ? linjeKursistForm(antal, id) : undefined}
          invalidateData={refetch}
        />
      </div>
    </ModalProvider>
  );
}

const defaultSortBy: SortBy<KursusholdPersonKursistField>[] = [
  {
    direction: "asc",
    field: "person.fornavne",
  },
];
